/**
 * Consistent color themes for charts and visualizations
 */

export interface ColorTheme {
  name: string;
  primary: string[];
  secondary: string[];
  accent: string[];
  status: {
    success: string;
    warning: string;
    error: string;
    info: string;
  };
  gradients: {
    primary: string;
    secondary: string;
    success: string;
    warning: string;
    error: string;
  };
}

export const DEFAULT_THEME: ColorTheme = {
  name: 'default',
  primary: [
    '#007bff', // Blue
    '#28a745', // Green
    '#ffc107', // Yellow
    '#dc3545', // Red
    '#6f42c1', // Purple
    '#17a2b8', // Cyan
    '#fd7e14', // Orange
    '#e83e8c', // Pink
    '#6c757d', // Gray
    '#343a40'  // Dark
  ],
  secondary: [
    '#6c9bd1', // Light Blue
    '#5cb85c', // Light Green
    '#f0ad4e', // Light Orange
    '#d9534f', // Light Red
    '#8e6ab8', // Light Purple
    '#5bc0de', // Light Cyan
    '#ff8c00', // Dark Orange
    '#ff69b4', // Hot Pink
    '#95a5a6', // Light Gray
    '#495057'  // Medium Dark
  ],
  accent: [
    '#0056b3', // Dark Blue
    '#1e7e34', // Dark Green
    '#e0a800', // Dark Yellow
    '#bd2130', // Dark Red
    '#59359a', // Dark Purple
    '#138496', // Dark Cyan
    '#e55100', // Dark Orange
    '#c2185b', // Dark Pink
    '#495057', // Medium Gray
    '#212529'  // Very Dark
  ],
  status: {
    success: '#28a745',
    warning: '#ffc107',
    error: '#dc3545',
    info: '#17a2b8'
  },
  gradients: {
    primary: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
    secondary: 'linear-gradient(135deg, #6c9bd1 0%, #007bff 100%)',
    success: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
    warning: 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)',
    error: 'linear-gradient(135deg, #dc3545 0%, #bd2130 100%)'
  }
};

const TRANSFER_TYPE_COLORS: Record<string, string> = {
  'SFTP Receive': '#007bff',
  'SFTP Send': '#28a745',
  'SFTP Pickup': '#ffc107',
  'SFTP Drop': '#dc3545'
};

/**
 * Get color by index from theme
 */
function getThemeColor(index: number, theme: ColorTheme = DEFAULT_THEME): string {
  return theme.primary[index % theme.primary.length];
}

/**
 * Get colors for transfer types
 */
export function getTransferTypeColor(transferType: string): string {
  return TRANSFER_TYPE_COLORS[transferType] || DEFAULT_THEME.primary[0];
}

/**
 * Generate color palette for a specific number of items
 */
export function generateColorPalette(count: number, theme: ColorTheme = DEFAULT_THEME): string[] {
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    colors.push(getThemeColor(i, theme));
  }
  return colors;
}